import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { okJSON } from './api'
import { holeTypeLabel, materialTypeLabel, classificationLabel, gradingLabel, stopReasonLabel } from './i18nText'

// Cache de módulo para catálogos que várias telas leem e quase nunca mudam (uma busca
// por sessão). Falha não derruba a tela: quem lê cai na i18n local ou no código cru, e
// a próxima montagem tenta de novo depois de RETRY_MS.
const RETRY_MS = 60000

function createCache(url, normalize) {
  let value = null
  let loadedAt = 0
  let failedAt = 0
  let inflight = null
  const listeners = new Set()
  const emit = () => listeners.forEach(fn => fn())

  return {
    get: () => value,
    subscribe(fn) {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    set(raw) {
      value = normalize(raw)
      loadedAt = Date.now()
      failedAt = 0
      emit()
    },
    invalidate() { loadedAt = 0 },
    load({ force = false } = {}) {
      if (inflight) return inflight
      if (!force && (loadedAt || (failedAt && Date.now() - failedAt < RETRY_MS))) return Promise.resolve(value)
      inflight = okJSON(url)
        .then(d => {
          value = normalize(d)
          loadedAt = Date.now()
          failedAt = 0
          emit()
          return value
        })
        .catch(err => {
          failedAt = Date.now()
          throw err
        })
        .finally(() => { inflight = null })
      return inflight
    },
  }
}

const textMap = (m) => (m && typeof m === 'object' && !Array.isArray(m) ? m : {})

const entries = (arr) => (Array.isArray(arr) ? arr : [])
  .filter(e => e && typeof e.code === 'string')
  .map(e => ({ code: e.code, labels: textMap(e.labels), active: e.active !== false }))

export function normalizeMaterialCatalog(d) {
  if (!d || typeof d !== 'object') return null
  return {
    version: d.version ?? null,
    types: (Array.isArray(d.types) ? d.types : [])
      .filter(t => t && typeof t.code === 'string')
      .map(t => ({
        code: t.code,
        labels: textMap(t.labels),
        classifications: entries(t.classifications),
        gradings: entries(t.gradings),
      })),
  }
}

function normalizeStopReasons(d) {
  return (Array.isArray(d) ? d : [])
    .filter(r => r && typeof r.code === 'string')
    .map(r => ({ id: r.id, code: r.code, label: r.label || '', labels: textMap(r.labels), bucket: r.bucket || '' }))
}

export const materialCatalogCache = createCache('/api/material-catalog', normalizeMaterialCatalog)
export const stopReasonsCache = createCache('/api/stop-reasons', normalizeStopReasons)

function useCache(cache) {
  const value = useSyncExternalStore(cache.subscribe, cache.get)
  useEffect(() => { cache.load().catch(() => {}) }, [cache])
  return value
}

export function useMaterialCatalog() {
  return useCache(materialCatalogCache)
}

export function useStopReasons() {
  return useCache(stopReasonsCache)
}

// useCodeLabels: rótulos dos códigos fixos do 0.8 no idioma da UI. O catálogo da mina
// vence a i18n local quando tem o código (a mina pode renomear).
export function useCodeLabels() {
  const { i18n } = useTranslation()
  const catalog = useMaterialCatalog()
  const lang = i18n.language
  return useMemo(() => ({
    holeType: (code) => holeTypeLabel(code, lang),
    materialType: (code) => materialTypeLabel(code, catalog, lang),
    classification: (code, type) => classificationLabel(code, type, catalog, lang),
    grading: (code, type) => gradingLabel(code, type, catalog, lang),
  }), [catalog, lang])
}

export function useStopReasonLabel() {
  const { i18n } = useTranslation()
  const reasons = useStopReasons()
  const lang = i18n.language
  return useMemo(() => (code, deviceText) => stopReasonLabel(code, deviceText, reasons, lang), [reasons, lang])
}
